/* eslint-disable no-unused-vars */
'use client'

import { create, StoreApi, UseBoundStore } from 'zustand'

import { AccountStatus } from '@/constants/account'
import { getChatbotLimitReach } from '@/services/chatbot-config-service'

export enum InstallPlatform {
  FACEBOOK_MESSENGER = 'Facebook Messenger',
  WORDPRESS = 'Wordpress',
}

interface IDuration {
  days: number
  hours: number
  minutes: number
}

export type IAccountDetail = {
  status: AccountStatus
  softLockDuration?: IDuration
  hardLockDuration?: IDuration
}

export interface IPublicRealtimeVoiceChatLimitStatus {
  dailyLimitReached: boolean
  monthlyLimitReached: boolean
}

export interface ILimitedProps {
  currentUsage: {
    uploadFile: number
    website: number
    faq: number
    question: number
    voice?: {
      dailyUsageSeconds: number
      monthlyUsageSeconds: number
    }
    keyword: number
    member: number
    mcpServer: number
  }
  limitUsage: {
    uploadFile: number
    website: number
    faq: number
    question: number
    apiKey: number
    voice?: {
      dailyLimitSeconds: number
      monthlyLimitSeconds: number
      sessionLimitSeconds: number
    }
    keyword: number
    member: number
    mcpServer: number
  }
  keywordManagement: boolean
  humanCollaboration: boolean
  welcomeQuestions: number
  platforms: InstallPlatform[]
  warningLimit: number
  escalationCount: number
  canUseVoiceChat?: boolean
  accountDetail: IAccountDetail
  canUseSupportEmail?: boolean
  canUseCollectContactInfo?: boolean
  canExportAnalyticsData?: boolean
  canExportConversations?: boolean
  canUseApiKeys?: boolean,
  canUseMCPSevers?: boolean,
  canUseOcrTraining?: boolean,
  canUseTerminologyMapping?: boolean
}

const useLimitReachedStore: UseBoundStore<
  StoreApi<{
    limitReach: ILimitedProps | undefined | null
    fetch: () => Promise<void>
    setLimitReach: (data: ILimitedProps) => void
    incrementEscalationCount: () => void
    decrementEscalationCount: () => void
    incrementFaqCurrentUsage: () => void
    decrementFaqCurrentUsage: () => void
    resetFaqCurrentUsage: () => void
    isFaqLimitReached: () => boolean
    setAccountDetail: (detail: IAccountDetail) => void
    incrementKeywordCurrentUsage: () => void
    decrementKeywordCurrentUsage: () => void
    isKeywordLimitReached: () => boolean
    resetKeywordCurrentUsage: () => void
    isOrganizationMemberLimitReached: () => boolean
    isMcpServerLimitReached: () => boolean
  }>
> = create((set) => ({
  limitReach: undefined,
  setLimitReach: (limitReach: ILimitedProps | null) => set({ limitReach }),
  fetch: async () => {
    const res = await getChatbotLimitReach()
    set({ limitReach: res.data })
  },
  incrementEscalationCount: () => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return
    set({ limitReach: { ...state.limitReach, escalationCount: state.limitReach.escalationCount + 1 } })
  },
  decrementEscalationCount: () => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return
    set({ limitReach: { ...state.limitReach, escalationCount: state.limitReach.escalationCount - 1 } })
  },
  incrementFaqCurrentUsage: () => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return
    set({
      limitReach: {
        ...state.limitReach,
        currentUsage: { ...state.limitReach.currentUsage, faq: state.limitReach.currentUsage.faq + 1 },
      },
    })
  },
  decrementFaqCurrentUsage: () => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return
    set({
      limitReach: {
        ...state.limitReach,
        currentUsage: { ...state.limitReach.currentUsage, faq: state.limitReach.currentUsage.faq - 1 },
      },
    })
  },
  resetFaqCurrentUsage: () => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return
    set({
      limitReach: {
        ...state.limitReach,
        currentUsage: { ...state.limitReach.currentUsage, faq: 0 },
      },
    })
  },
  isFaqLimitReached: () => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return false
    const { currentUsage, limitUsage } = state.limitReach

    return currentUsage?.faq >= limitUsage?.faq
  },
  setAccountDetail: (detail: IAccountDetail) => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return
    set({ limitReach: { ...state.limitReach, accountDetail: detail } })
  },
  incrementKeywordCurrentUsage: () => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return
    set({
      limitReach: {
        ...state.limitReach,
        currentUsage: { ...state.limitReach.currentUsage, keyword: state.limitReach.currentUsage.keyword + 1 },
      },
    })
  },
  decrementKeywordCurrentUsage: () => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return
    set({
      limitReach: {
        ...state.limitReach,
        currentUsage: { ...state.limitReach.currentUsage, keyword: state.limitReach.currentUsage.keyword - 1 },
      },
    })
  },
  resetKeywordCurrentUsage: () => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return
    set({
      limitReach: {
        ...state.limitReach,
        currentUsage: { ...state.limitReach.currentUsage, keyword: 0 },
      },
    })
  },
  isKeywordLimitReached: () => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return false
    const { currentUsage, limitUsage } = state.limitReach

    return currentUsage?.keyword >= limitUsage?.keyword
  },
  isOrganizationMemberLimitReached: () => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return false
    const { currentUsage, limitUsage } = state.limitReach

    return currentUsage?.member >= limitUsage?.member
  },
  isMcpServerLimitReached: () => {
    const state = useLimitReachedStore.getState()
    if (!state.limitReach) return false
    const { currentUsage, limitUsage } = state.limitReach

    return currentUsage?.mcpServer >= limitUsage?.mcpServer
  },
}))

const getUsagePercentage = () => {
  const limitReach = useLimitReachedStore.getState().limitReach
  if (!limitReach) return null
  const { currentUsage, limitUsage } = limitReach

  return {
    uploadFile: currentUsage?.uploadFile / limitUsage?.uploadFile,
    website: currentUsage?.website / limitUsage?.website,
    faq: currentUsage?.faq / limitUsage?.faq,
    question: currentUsage?.question / limitUsage?.question,
    warningLimit: limitReach?.warningLimit,
  }
}

const getLimitUsage = () => {
  const limitReach = useLimitReachedStore.getState().limitReach
  return limitReach
    ? {
        currentUsage: limitReach.currentUsage,
        limitUsage: limitReach.limitUsage,
        warningLimit: limitReach?.warningLimit,
        keywordManagement: limitReach?.keywordManagement,
        humanCollaboration: limitReach?.humanCollaboration,
        welcomeQuestions: limitReach?.welcomeQuestions,
        platforms: limitReach?.platforms,
        escalationCount: limitReach?.escalationCount,
      }
    : null
}

const getAccountDetail = (): IAccountDetail | undefined => {
  const limitReach = useLimitReachedStore.getState().limitReach
  return limitReach?.accountDetail
}

export { getAccountDetail, getLimitUsage, getUsagePercentage, useLimitReachedStore }
